﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { createUseStyles } from "react-jss";
import { useIntl } from "~base/Internationalization/IntlContext";
import type { SoPNLConsilioTheme } from "~jrad/Styling/SoPNLConsilioTheme";
import type { SelfServiceLoginViewAction } from "../Store";
import type { SelfServiceLoginViewState } from "../Types";
import SelfServicePageNavigationControls from "../ChildComponents/PageNavigationControls";
import LabelCheckbox from "~base/Components/Primitives/LabelCheckbox";
import Expander from "~base/Components/Layout/Expander";
import classNames from "classnames";
import { useSettings } from "~base/Settings/SettingsContext";
import { SoPThemeConstants } from "~irad/Styling/SoPThemeConstants";
import type { SelfServiceLoginInfoTenant } from "~jrad/Services/API/Account/GetSelfServiceLoginInfo";

// TODO: Replace with actual SoPThemeConstantsV2 when PR !21491 is merged
const SoPThemeConstantsV2 = {
    Typography: {
        Paragraph: {
            fontFamily: SoPThemeConstants.defaultFont.replace(
                "Whitney SSM A, Whitney SSm B, ",
                ""
            ),
            fontSize: "18px",
            fontWeight: "normal",
        },
        Label: {
            fontFamily: SoPThemeConstants.defaultFont.replace(
                "Whitney SSM A, Whitney SSm B, ",
                ""
            ),
            fontSize: "18px",
            fontWeight: "bold",
        },
    },
};

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

export interface SelfServiceLoginPageTermsOfServiceProps {
    isLoading: boolean;
    state: SelfServiceLoginViewState;
    dispatch: React.Dispatch<SelfServiceLoginViewAction>;
    postRegistrationRequest: () => void;
    tenant?: SelfServiceLoginInfoTenant;
}

const useStyles = createUseStyles((theme: SoPNLConsilioTheme) => ({
    container: {},
    header: {
        color: theme.constants.textColor,
        fontFamily: theme.constants.headerFont,
        fontSize: "24px",
        fontWeight: "normal",
    },
    desc: {
        ...SoPThemeConstantsV2.Typography.Paragraph,
        color: theme.constants.textColor,
        whiteSpace: "pre-line",
    },
    agreementCheckbox: {
        margin: "30px 0",
    },
    agreementCheckboxLabel: {
        color: theme.constants.textColor,
        ...SoPThemeConstantsV2.Typography.Label,

        "& a": {
            color: "#009fdf",

            "&:hover": {
                color: "#002d72",
            },
        },
    },
    agreementSummaryContainer: {
        margin: "10px 0",
        border: "solid 1px #009fdf",
        opacity: 0,

        transition: "opacity 500ms ease",
    },
    agreementSummaryContainerOpen: {
        opacity: 1,
    },
    agreementSummaryToggle: {
        display: "flex",
        alignItems: "center",

        width: "fit-content",

        cursor: "pointer",
        color: theme.constants.primaryColor,

        "&:hover": {
            color: "#002d72",

            "& $agreementSummaryToggleIconButton": {
                backgroundColor: "#002d72",
            },
        },

        marginTop: "15px",
        marginBottom: "5px",
    },
    agreementSummaryToggleIcon: {
        fontFamily: theme.constants.defaultFont,
        fontSize: "28px",
    },
    agreementSummaryToggleIconButton: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        width: "14px",
        height: "14px",
        padding: "5px",
        borderRadius: "50%",

        color: "#ffffff",
        backgroundColor: theme.constants.primaryColor,
    },
    agreementSummaryToggleText: {
        fontFamily: theme.constants.defaultFont,
        fontSize: "18px",
        textDecoration: "underline",
        marginLeft: "10px",
    },
    agreementSummaryText: {
        fontFamily: theme.constants.defaultFont,
        fontSize: "15px",
        color: theme.constants.textColor,
        whiteSpace: "pre-line",
        padding: "40px 25px 40px 25px",
    },
}));

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

const PageTermsOfService: React.FunctionComponent<
    SelfServiceLoginPageTermsOfServiceProps
> = ({ isLoading, state, dispatch, postRegistrationRequest, tenant }) => {
    const intl = useIntl();
    const classes = useStyles();
    const settings = useSettings();

    const registrationTenant = tenant?.TenantName;

    const tenantName = settings.valueAsString("detectedTenantName");
    const tenantDiplayName =
        intl.tryFormatText("SoPNL.UI.TenantDisplayName") ??
        registrationTenant ??
        tenantName;

    const [showAgreementSummary, setShowAgreementSummary] =
        React.useState(false);

    return (
        <>
            <h2 className={classes.header}>
                {intl.formatText("SoPNL.UI.SelfServiceLogin.Agreement.Title")}
            </h2>
            <p
                className={classes.desc}
                dangerouslySetInnerHTML={{
                    __html: intl.formatText(
                        "SoPNL.UI.SelfServiceLogin.Agreement.Body",
                        ...[tenantDiplayName]
                    ),
                }}
            />
            <LabelCheckbox
                testIdentifier="AcceptTermsOfService"
                className={classes.agreementCheckbox}
                labelClassName={classes.agreementCheckboxLabel}
                padding={[]}
                size="Medium"
                label={intl.formatHtml(
                    "SoPNL.UI.SelfServiceLogin.Agreement.CheckboxLabel",
                    state.loginInfo?.AgreementLink
                )}
                checked={state.requestData.hasAcceptedToS}
                onChange={(checked) => {
                    dispatch({
                        type: "setRequestData",
                        key: "hasAcceptedToS",
                        value: checked,
                    });
                }}
            />
            <div
                className={classes.agreementSummaryToggle}
                onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();

                    setShowAgreementSummary(!showAgreementSummary);
                }}
            >
                <div className={classes.agreementSummaryToggleIconButton}>
                    <div className={classes.agreementSummaryToggleIcon}>
                        {showAgreementSummary ? "-" : "+"}
                    </div>
                </div>
                <div className={classes.agreementSummaryToggleText}>
                    {intl.formatText(
                        "SoPNL.UI.SelfServiceLogin.Agreement.ToggleLink"
                    )}
                </div>
            </div>
            <div
                className={classNames(
                    classes.agreementSummaryContainer,
                    showAgreementSummary &&
                        classes.agreementSummaryContainerOpen
                )}
            >
                <Expander
                    testIdentifier="AgreementSummary"
                    expanded={showAgreementSummary}
                >
                    <div
                        className={classes.agreementSummaryText}
                        dangerouslySetInnerHTML={{
                            __html: state.loginInfo?.AgreementText,
                        }}
                    />
                </Expander>
            </div>
            <SelfServicePageNavigationControls
                state={state}
                dispatch={dispatch}
                postRegistrationRequest={postRegistrationRequest}
                disablePrev
                disableNext={!state.requestData.hasAcceptedToS || isLoading}
            />
        </>
    );
};

export default PageTermsOfService;
